import { cronForEvery, everyForCron } from "@nova-ai/shared"
import { useLayoutEffect, useRef, useState } from "react"

import {
  ScheduleConflictError,
  type ScheduledJob,
  useScheduledJobPreview,
  useUpdateScheduledJob,
} from "../services/use-scheduled-jobs"
import { useDebounce } from "./use-debounce"
import { useScheduleActions } from "./use-schedule-actions"

type FormMode = "basic" | "advanced"
type FormState = {
  baseJob: ScheduledJob
  mode: FormMode
  cronExpression: string
  timeZone: string
  enabled: boolean
}
function stateFor(job: ScheduledJob): FormState {
  return {
    baseJob: job,
    mode: everyForCron(job.schedule.cron) ? "basic" : "advanced",
    cronExpression: job.schedule.cron,
    timeZone: job.schedule.timeZone,
    enabled: job.enabled,
  }
}
function draftErrors(cron: string, zone: string) {
  let timeZone: string | null = null
  try {
    if (!zone.trim()) {
      throw new Error()
    }
    new Intl.DateTimeFormat("en", { timeZone: zone.trim() })
  } catch {
    timeZone = "Enter a valid IANA timezone, such as Europe/Bratislava."
  }
  const cronExpression =
    cron.trim().split(/\s+/).length === 5 ? null : "Enter a five-field cron expression."
  return { cronExpression, timeZone }
}

export function useScheduleEditForm(initialJob: ScheduledJob, canEdit: boolean) {
  const [state, setState] = useState(() => stateFor(initialJob))
  const [touched, setTouched] = useState({ cronExpression: false, timeZone: false })
  const [conflict, setConflict] = useState<ScheduleConflictError | null>(null)
  const busy = useRef(false)
  const updateMutation = useUpdateScheduledJob()
  const { notify } = useScheduleActions()
  const { baseJob, cronExpression, timeZone, enabled } = state
  const isDirty =
    cronExpression.trim() !== baseJob.schedule.cron ||
    timeZone.trim() !== baseJob.schedule.timeZone ||
    enabled !== baseJob.enabled
  const { preview, previewError, previewLoading, fieldErrors } = useDraftPreview(state, touched)
  useLayoutEffect(() => {
    if (
      baseJob.id === initialJob.id &&
      (initialJob.schedule.version <= baseJob.schedule.version || isDirty)
    ) {
      return
    }
    setState(stateFor(initialJob))
    setTouched({ cronExpression: false, timeZone: false })
    setConflict(null)
  }, [initialJob, baseJob.id, baseJob.schedule.version, isDirty])
  const change = (patch: Partial<Omit<FormState, "baseJob">>) => {
    if (busy.current || !canEdit) {
      return
    }
    setState((current) => ({ ...current, ...patch }))
    updateMutation.reset()
  }
  const saveDraft = () => {
    if (!canEdit || !preview || !isDirty || conflict || busy.current || initialJob.manualOnly) {
      return
    }
    busy.current = true
    updateMutation.mutate(
      {
        id: baseJob.id,
        expectedVersion: baseJob.schedule.version,
        cronExpression: preview.schedule.cron,
        timeZone: preview.schedule.timeZone,
        enabled,
      },
      {
        onSuccess: ({ current }) => {
          setState(stateFor(current))
          setConflict(null)
          notify({ message: `Schedule saved · version ${current.schedule.version}` })
        },
        onError: (error) => {
          if (error instanceof ScheduleConflictError) {
            setConflict(error)
          }
        },
        onSettled: () => {
          busy.current = false
        },
      }
    )
  }
  return {
    ...state,
    isDirty,
    preview,
    previewError,
    previewLoading,
    fieldErrors,
    conflict,
    updateMutation,
    basicCadence: everyForCron(cronExpression),
    canSave:
      canEdit &&
      isDirty &&
      Boolean(preview) &&
      !conflict &&
      !updateMutation.isPending &&
      !initialJob.manualOnly,
    setMode: (value: FormMode) =>
      change({
        mode: value,
        ...(value === "basic" && !everyForCron(cronExpression)
          ? { cronExpression: cronForEvery("5m") }
          : {}),
      }),
    setCronExpression: (value: string) => change({ cronExpression: value }),
    setTimeZone: (value: string) => change({ timeZone: value }),
    setEnabled: (value: boolean) => {
      if (!initialJob.manualOnly) {
        change({ enabled: value })
      }
    },
    setBasicCadence: (value: string) => {
      if (value) {
        change({ cronExpression: cronForEvery(value) })
      }
    },
    blur: (field: keyof typeof touched) => setTouched((current) => ({ ...current, [field]: true })),
    discard: () => {
      if (busy.current) {
        return
      }
      setState(stateFor(initialJob))
      setConflict(null)
      setTouched({ cronExpression: false, timeZone: false })
      updateMutation.reset()
    },
    reloadConflict: () => {
      if (!conflict || busy.current) {
        return
      }
      setState((current) => ({ ...current, baseJob: conflict.current }))
      setConflict(null)
      updateMutation.reset()
    },
    saveDraft,
  }
}

function useDraftPreview(
  state: FormState,
  touched: { cronExpression: boolean; timeZone: boolean }
) {
  const { baseJob, cronExpression, timeZone } = state
  const debouncedCron = useDebounce(cronExpression.trim(), 300)
  const debouncedZone = useDebounce(timeZone.trim(), 300)
  const errors = draftErrors(cronExpression, timeZone)
  const isDebouncing = debouncedCron !== cronExpression.trim() || debouncedZone !== timeZone.trim()
  const canPreview = !isDebouncing && !errors.cronExpression && !errors.timeZone
  const previewQuery = useScheduledJobPreview(
    { id: baseJob.id, cronExpression: debouncedCron, timeZone: debouncedZone },
    !baseJob.manualOnly && canPreview
  )
  const preview =
    canPreview && !previewQuery.isError && !previewQuery.isFetching ? previewQuery.data : undefined
  const previewError = !isDebouncing ? (previewQuery.error?.message ?? null) : null
  const fieldErrors = visibleDraftErrors(errors, touched, previewError)

  return {
    preview,
    previewError,
    previewLoading: isDebouncing || previewQuery.isFetching,
    fieldErrors,
  }
}

function visibleDraftErrors(
  errors: ReturnType<typeof draftErrors>,
  touched: { cronExpression: boolean; timeZone: boolean },
  previewError: string | null
) {
  return {
    cronExpression: touched.cronExpression
      ? (errors.cronExpression ?? (errors.timeZone ? null : previewError))
      : null,
    timeZone: touched.timeZone ? errors.timeZone : null,
  }
}
